import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Clock,
  CalendarDays,
  ArrowRight,
  UserCheck,
  BellPlus,
  StickyNote,
} from 'lucide-react';
import { getTasks } from '../../../api/task';
import { formatDateAU, formatTimeAU } from '../../../utils/dateFormat';
import { formatAddress } from '../../../utils/addressFormatter';

/**
 * Future Reminders — a calendar view of reminders/tasks scheduled for a day AFTER
 * today. Reminders set for a later date deliberately stay OFF the dashboard's
 * "Tasks & Reminders" list until their day arrives (so each morning an agent sees
 * only what's due that day); this modal lets them look ahead at what's coming up.
 *
 * The backend `due=upcoming` filter returns only items due strictly after today,
 * bucketed by the agency's local (Melbourne) day.
 */

// Local YYYY-MM-DD key for grouping items and matching calendar cells. Built from
// local date parts (not toISOString, which is UTC) so an item lands on the day the
// agent actually sees.
const dayKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const MONTH_LABEL = (d) =>
  d.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });

// Where a reminder/task deep-links to: the person's contact profile first, then
// the linked property, then the leads list. null = not clickable.
const taskLink = (task) => {
  if (task.contact_id) return `/contacts/${task.contact_id}`;
  if (task.related_property) return `/properties/${task.related_property}`;
  if (task.lead) return `/leads`;
  return null;
};

const FutureRemindersModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // First day of the month currently shown in the calendar grid.
  const [monthCursor, setMonthCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedKey, setSelectedKey] = useState(null); // clicked calendar day

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    setSelectedKey(null);
    // Reset the calendar to the current month each time the modal is opened.
    const now = new Date();
    setMonthCursor(new Date(now.getFullYear(), now.getMonth(), 1));

    getTasks({ completed: false, due: 'upcoming' })
      .then((res) => {
        if (!active) return;
        const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
        const sorted = [...data].sort(
          (a, b) =>
            new Date(a.due_date || a.reminder_date || '9999-12-31') -
            new Date(b.due_date || b.reminder_date || '9999-12-31')
        );
        setItems(sorted);
      })
      .catch(() => {
        if (active) setItems([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open]);

  // Group items by local day for calendar dots + the day list.
  const byDay = useMemo(() => {
    const map = {};
    items.forEach((it) => {
      const raw = it.due_date || it.reminder_date;
      if (!raw) return;
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return;
      const key = dayKey(d);
      (map[key] = map[key] || []).push(it);
    });
    return map;
  }, [items]);

  // Build the month grid (Monday-first, matching the dashboard calendar). Leading
  // blanks pad the first week.
  const monthDays = useMemo(() => {
    const year = monthCursor.getFullYear();
    const month = monthCursor.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const cells = [];
    const firstWeekday = firstDay.getDay(); // 0 = Sun
    const lead = firstWeekday === 0 ? 6 : firstWeekday - 1; // Monday-first
    for (let i = 0; i < lead; i += 1) cells.push(null);
    for (let day = 1; day <= lastDay.getDate(); day += 1) {
      cells.push(new Date(year, month, day));
    }
    return cells;
  }, [monthCursor]);

  const todayKey = dayKey(new Date());
  const listItems = selectedKey ? byDay[selectedKey] || [] : items;

  const goToItem = (task) => {
    const link = taskLink(task);
    onClose?.();
    if (link) navigate(link);
    else navigate('/tasks');
  };

  const shiftMonth = (delta) => {
    setMonthCursor((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1));
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-3 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-teal-50">
              <CalendarDays className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-base sm:text-lg">Future Reminders</h3>
              <p className="text-xs text-gray-500">Reminders scheduled for an upcoming day</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-4 p-4 sm:p-6">
          {/* Calendar */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => shiftMonth(-1)}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
                aria-label="Previous month"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm font-bold text-gray-800">{MONTH_LABEL(monthCursor)}</span>
              <button
                onClick={() => shiftMonth(1)}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
                aria-label="Next month"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2">
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
                <div
                  key={i}
                  className="text-center text-[10px] font-bold text-gray-400 uppercase tracking-wider"
                >
                  {d}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {monthDays.map((d, idx) => {
                if (!d) return <div key={`empty-${idx}`} />;
                const key = dayKey(d);
                const count = byDay[key]?.length || 0;
                const isToday = key === todayKey;
                const isSelected = key === selectedKey;
                const hasItems = count > 0;
                return (
                  <button
                    key={key}
                    onClick={() => hasItems && setSelectedKey(isSelected ? null : key)}
                    disabled={!hasItems}
                    className={`relative aspect-square flex flex-col items-center justify-center text-xs rounded-lg transition-all
                      ${isSelected
                        ? 'bg-teal-500 text-white font-bold shadow-sm'
                        : hasItems
                          ? 'bg-teal-50 text-teal-800 font-semibold hover:bg-teal-100 cursor-pointer'
                          : isToday
                            ? 'text-gray-800 font-bold border border-teal-200'
                            : 'text-gray-400'}`}
                  >
                    {d.getDate()}
                    {hasItems && (
                      <span
                        className={`mt-0.5 w-1.5 h-1.5 rounded-full ${
                          isSelected ? 'bg-white' : 'bg-teal-500'
                        }`}
                      />
                    )}
                  </button>
                );
              })}
            </div>

            {selectedKey && (
              <button
                onClick={() => setSelectedKey(null)}
                className="mt-3 text-xs font-medium text-teal-600 hover:underline"
              >
                Show all upcoming
              </button>
            )}
          </div>

          {/* List */}
          <div className="mt-4 md:mt-0 md:border-l md:border-gray-100 md:pl-4">
            <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
              {selectedKey
                ? formatDateAU(selectedKey)
                : `All upcoming (${items.length})`}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-10 text-gray-400 text-sm">
                <div className="w-6 h-6 border-2 border-gray-200 border-t-teal-500 rounded-full animate-spin mr-2" />
                Loading…
              </div>
            ) : listItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-300">
                <BellPlus className="w-12 h-12 mb-2 stroke-1" />
                <span className="text-sm text-gray-400">No upcoming reminders</span>
              </div>
            ) : (
              <div className="space-y-2 max-h-[46vh] overflow-y-auto no-scrollbar pr-1">
                {listItems.map((task) => {
                  const raw = task.due_date || task.reminder_date;
                  return (
                    <div
                      key={task.id}
                      onClick={() => goToItem(task)}
                      className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-teal-200 hover:bg-teal-50/40 transition-all cursor-pointer group"
                    >
                      <div
                        className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
                          task.priority === 'high'
                            ? 'bg-red-500'
                            : task.priority === 'medium'
                              ? 'bg-orange-400'
                              : 'bg-green-400'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        {task.contact_name || task.lead_name ? (
                          <>
                            <div className="text-sm font-bold text-gray-900 truncate flex items-center gap-1.5 group-hover:text-teal-700">
                              <UserCheck className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                              {task.contact_name || task.lead_name}
                            </div>
                            <div className="text-[11px] text-gray-500 truncate">{task.task_name}</div>
                          </>
                        ) : (
                          <div className="text-sm font-bold text-gray-900 truncate group-hover:text-teal-700">
                            {task.task_name}
                          </div>
                        )}
                        {task.property_address && (
                          <div className="text-[10px] text-gray-500 truncate mt-0.5">
                            {formatAddress(task.property_address, { short: true })}
                          </div>
                        )}
                        {task.latest_note?.text && (
                          <div className="flex items-center gap-1 text-[10px] text-amber-600 truncate mt-0.5">
                            <StickyNote className="w-2.5 h-2.5 flex-shrink-0" />
                            <span className="truncate">{task.latest_note.text}</span>
                          </div>
                        )}
                        <div className="flex items-center mt-1 text-[10px] text-gray-500 gap-1">
                          <Clock className="w-3 h-3" />
                          <span>
                            {formatDateAU(raw)}
                            {task.source === 'reminder' && raw ? `, ${formatTimeAU(raw)}` : ''}
                          </span>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FutureRemindersModal;
